import React from 'react';
import Article from '../components/Article';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import CodeBlock from '../components/CodeBlock';

function ModernJavaScriptTechniques() {
  const articleData = {
    id: 1,
    image: "https://images.unsplash.com/photo-1555066931-4365d14bab8c?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1200&q=80",
    category: "JavaScript",
    title: "Modern JavaScript Techniques You Should Know in 2023",
    excerpt: "Discover the latest JavaScript features and patterns that are revolutionizing web development today.",
    tags: ["JavaScript", "ES6", "WebDev", "Frontend"],
    content: (
      <>
        <p>JavaScript continues to evolve rapidly, with new features and capabilities being added regularly. Staying current with these advancements is essential for any developer looking to build modern, efficient web applications. In this article, we'll explore the most impactful JavaScript techniques and features that every developer should be familiar with in 2023.</p>
        
        <h2>1. Optional Chaining</h2>
        <p>Optional chaining is a game-changer for handling potentially undefined properties in nested objects. Instead of writing verbose conditional checks, you can use the <code>?.</code> operator:</p>
        <CodeBlock language="javascript" fileName="example.js">
          {`// Instead of this:
const streetName = user && user.address && user.address.street;

// You can do this:
const streetName = user?.address?.street;`}
        </CodeBlock>
        <p>This elegant syntax prevents errors when accessing properties of potentially undefined values, making your code both cleaner and safer.</p>
        
        <h2>2. Nullish Coalescing Operator</h2>
        <p>The nullish coalescing operator (<code>??</code>) provides a cleaner way to provide default values when dealing with <code>null</code> or <code>undefined</code>:</p>
        <CodeBlock language="javascript" fileName="example.js">
          {`// Old way:
const count = value !== null && value !== undefined ? value : defaultValue;

// New way:
const count = value ?? defaultValue;`}
        </CodeBlock>
        <p>Unlike the logical OR operator (<code>||</code>), the nullish coalescing operator only falls back to the default value for <code>null</code> or <code>undefined</code>, not for other falsy values like <code>0</code> or empty strings.</p>
        
        <h2>3. Array and Object Destructuring</h2>
        <p>Destructuring makes extracting values from arrays and objects more concise and readable:</p>
        <CodeBlock language="javascript" fileName="example.js">
          {`// Array destructuring
const [first, second, ...rest] = ['apple', 'banana', 'cherry', 'date'];
console.log(first);  // 'apple'
console.log(rest);   // ['cherry', 'date']

// Object destructuring with default values
const { name, age = 25, role = 'Developer' } = person;`}
        </CodeBlock>
        <p>Destructuring is particularly useful for working with function parameters, making function signatures much cleaner and more flexible.</p>
        
        <h2>4. Async/Await for Cleaner Asynchronous Code</h2>
        <p>Async/await has transformed the way we handle asynchronous operations, making code that works with Promises much more readable:</p>
        <CodeBlock language="javascript" fileName="example.js">
          {`// Using Promises with .then()
fetchUserData()
.then(userData => fetchUserPosts(userData.id))
.then(posts => displayPosts(posts))
.catch(error => handleError(error));

// Using async/await
async function loadUserContent() {
try {
    const userData = await fetchUserData();
    const posts = await fetchUserPosts(userData.id);
    displayPosts(posts);
} catch (error) {
    handleError(error);
}
            }`}
        </CodeBlock>
        <p>This approach makes asynchronous code look and behave more like synchronous code, which is easier to understand and maintain.</p>
        
        <h2>5. ES Modules</h2>
        <p>ES Modules have become the standard way to organize JavaScript code, providing a clean syntax for importing and exporting functionality:</p>
        <CodeBlock language="javascript" fileName="example.js">
          {`// Exporting
export const PI = 3.14159;
export function calculateArea(radius) {
return PI * radius * radius;
}
export default class Circle { /* ... */ }

// Importing
import Circle, { PI, calculateArea } from './circle.js';`}
        </CodeBlock>
        <p>Using ES Modules helps create more maintainable codebases with clear dependencies between different parts of your application.</p>
        
        <h2>6. Array Methods for Functional Programming</h2>
        <p>Modern JavaScript encourages a more functional programming style, particularly with array methods like <code>map</code>, <code>filter</code>, and <code>reduce</code>:</p>
        <CodeBlock language="javascript" fileName="example.js">
          {`const numbers = [1, 2, 3, 4, 5, 6];

// Transform array
const doubled = numbers.map(n => n * 2);

// Filter array
const evens = numbers.filter(n => n % 2 === 0);

// Reduce array to a single value
const sum = numbers.reduce((total, n) => total + n, 0);`}
        </CodeBlock>
        <p>These methods allow you to express transformations on data more declaratively, focusing on what you want to accomplish rather than how to do it.</p>
        
        <h2>7. Private Class Fields and Methods</h2>
        <p>JavaScript classes now support private fields and methods using the <code>#</code> prefix:</p>
        <CodeBlock language="javascript" fileName="example.js">
          {`class BankAccount {
    #balance = 0;  // Private field

    #validateAmount(amount) {  // Private method
        if (amount <= 0) throw new Error('Amount must be positive');
    }

    deposit(amount) {
        this.#validateAmount(amount);
        this.#balance += amount;
        return this.#balance;
    }

    get balance() {
        return this.#balance;
    }
}`}
        </CodeBlock>
        <p>This feature improves encapsulation in class-based code, making it clearer which aspects of a class are part of its public API and which are internal implementation details.</p>
        
        <h2>Conclusion</h2>
        <p>The JavaScript ecosystem continues to evolve rapidly, with each new addition making the language more powerful and developer-friendly. By incorporating these modern techniques into your toolkit, you'll be able to write cleaner, more maintainable, and more robust code.</p>
        <p>Remember that while it's important to stay current with new features, it's equally important to understand when and why to use them. The best developers know not just how to use new tools, but when they're the right solution for a particular problem.</p>
      </>
    )
  };

  return (
    <div className="article-page">
      <Navbar />
      <main>
        <Article article={articleData} />
        <ol className="workflow-steps">
          {/* ...list items... */}
        </ol>
      </main>
      <Footer />
    </div>
  );
}

export default ModernJavaScriptTechniques;
